import { type JSX } from 'react'
import {
  DrawerInfoButton,
  DrawerInfoPanel,
  useDrawerInfo,
  type DrawerInfoContent,
} from './DrawerInfo.js'

export interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  title: string
  /** What this drawer does, behind an ⓘ button in the header. Omit and no
   *  button renders — see DrawerInfo.tsx. */
  info?: DrawerInfoContent
  children: JSX.Element
}

export default function Drawer({ isOpen, onClose, title, info, children }: DrawerProps): JSX.Element | null {
  // Before the early return — hooks can't run conditionally.
  const drawerInfo = useDrawerInfo(isOpen)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#16273F] to-[#1e3a5f]">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <div className="flex items-center gap-1">
            {info && (
              <DrawerInfoButton
                shown={drawerInfo.shown}
                onToggle={drawerInfo.toggle}
                className="text-white/70 hover:bg-white/10 hover:text-white"
              />
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-white/70 hover:text-white transition-colors cursor-pointer rounded-lg p-1 hover:bg-white/10"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        {info && (
          <DrawerInfoPanel info={info} shown={drawerInfo.shown} onDismiss={drawerInfo.toggle} />
        )}
        <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50">
          {children}
        </div>
      </div>
    </div>
  )
}
